import React, { useEffect, useRef } from "react";
import dataFeed from "../support/chart";
import { widget } from "../charting_library";
import { useSearchParams } from "react-router-dom";
import { Box, Paper, useTheme } from "@mui/material";
import config from "../config/settings";
import { formatNumberHumanize } from "../utils/number";
import { chartColors } from "../theme";

function getLanguageFromURL() {
  const regex = new RegExp("[\\?&]lang=([^&#]*)");
  const results = regex.exec(window.location.search);
  return results === null
    ? null
    : decodeURIComponent(results[1].replace(/\+/g, " "));
}

const Chart = ({ pairAddress, price, isUSD }) => {
  const chartContainerRef = useRef();
  const chartInstanceRef = useRef(null);
  const theme = useTheme();

  const defaultProps = {
    pair: "ETHUSDC",
    symbol: "0x88e6a0c2ddd26feeb64f039a2c41296fcb3f5640",
    libraryPath: "/charting_library/",
    fullscreen: false,
    autosize: true,
    studiesOverrides: {},
  };

  useEffect(() => {
    if (pairAddress) {
      const widgetOptions = {
        symbol: pairAddress || defaultProps.symbol,
        datafeed: dataFeed(pairAddress, price, isUSD),
        interval:
          localStorage.getItem(
            "tradingview.chart.lastUsedTimeBasedResolution"
          ) || "1",
        container: chartContainerRef.current,
        library_path: defaultProps.libraryPath,
        toolbar_bg: theme.palette.background.paper,
        debug: config.isDevelopment,
        custom_css_url: "/custom.css",
        custom_formatters: {
          priceFormatterFactory: (price) => {
            return {
              format: (price, signPositive) => {
                return formatNumberHumanize(price, 2);
              },
            };
          },
        },
        overrides: {
          "mainSeriesProperties.style": 1,
          "paneProperties.background": theme.palette.background.paper,
          "paneProperties.backgroundGradientStartColor":
            theme.palette.background.paper,
          "paneProperties.backgroundGradientEndColor":
            theme.palette.background.paper,
        },
        theme: theme.palette.mode,
        loading_screen: {
          backgroundColor: theme.palette.background.paper,
          foregroundColor: theme.palette.background.paper,
          toolbar_bg: theme.palette.background.paper,
        },
        disabled_features: [
          "symbol_info",
          "header_widget_dom_node",
          "header_symbol_search",
          "symbol_search_hot_key",
          "header_compare",
          "border_around_the_chart",
          "control_bar",
          "timeframes_toolbar",
        ],
        enabled_features: ["seconds_resolution"],
        locale: getLanguageFromURL() || "en",
        fullscreen: defaultProps.fullscreen,
        autosize: defaultProps.autosize,
        studies_overrides: defaultProps.studiesOverrides,
      };

      // Only create new instance if one doesn't exist
      if (!chartInstanceRef.current) {
        chartInstanceRef.current = new widget(widgetOptions);
        chartInstanceRef.current.onChartReady(() => {
          chartInstanceRef.current.headerReady().then(() => {
            chartInstanceRef.current.applyOverrides({
              "paneProperties.background": theme.palette.background.paper,
              "paneProperties.backgroundGradientStartColor":
                theme.palette.background.paper,
              "paneProperties.backgroundGradientEndColor":
                theme.palette.background.paper,
            });
          });
        });
      }

      // Cleanup function
      return () => {
        if (chartInstanceRef.current) {
          chartInstanceRef.current.remove();
          chartInstanceRef.current = null;
        }
      };
    }
  }, [theme.palette.mode, pairAddress]); // Only re-run if theme or search params change

  return (
    <Paper
      elevation={2}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 0,
      }}
    >
      <Box
        ref={chartContainerRef}
        className="TVChartContainer"
        sx={{
          width: "100%",
          flex: 1,
          minHeight: 0,
          height: "100%",
          "& > div": {
            width: "100% !important",
            height: "100% !important",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          },
        }}
      />
    </Paper>
  );
};

export default Chart;
